import React, { lazy, Suspense, useEffect } from 'react';
import { Switch, Route, Redirect, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import browserHistory from '../history';
import { urls } from './Urls';
import { useDispatch, useSelector } from 'react-redux';
import { verificationPendingActions } from '../Store/Slices/VerificationPendingSlice';
import { isUserLoggedIn } from '../Util';
import Home from '../Modules/Home/Home';
import SkeletonLoader from '../Components/Loader/SkeletonLoader';
import ExclusiveBookPDP from '../Modules/ProductDetail/ExclusiveBookPDP/ExclusiveBookPDP';
import Error410Page from '../Components/Error410Page';

// Lazy loaded components
const LoginComponent = lazy(() => import('../Modules/Login/Login'));
const ForgotPassword = lazy(() => import('../Modules/ForgotPassword/ForgotPassword'));
const SignUp = lazy(() => import('../Modules/SignUp/SignUp'));
const ContactUs = lazy(() => import('../Modules/ContactUs/ContactUS'));
const FAQ = lazy(() => import('../Modules/FAQ/Faq'));
const Listing = lazy(() => import('../Modules/ListingPage/Listing'));
const ProductDetail = lazy(() => import('../Modules/ProductDetail/ProductDetail'));
const Viewer = lazy(() => import('../Modules/WebGLViewer/Viewer'));
const Editor = lazy(() => import('../Modules/Editor/Editor'));
const Customization = lazy(() => import('../Modules/Customization/Customization'));
const Services = lazy(() => import('../Modules/Services/Services'));
const MyCart = lazy(() => import('../Modules/MyCart/MyCart'));
const BookRestoration = lazy(() => import('../Modules/Services/BookRestoration/BookRestoration'));
const PDFPrinting = lazy(() => import('../Modules/Services/PDFPrinting/PDFPrinting'));
const ThesisBinding = lazy(() => import('../Modules/Services/ThesisBinding/ThesisBinding'));
const LeatherBinding = lazy(() => import('../Modules/Services/LeatherBinding/LeatherBinding'));
const OurCompany = lazy(() => import('../Modules/OurCompany/OurCompany'));
const MyAccount = lazy(() => import('../Modules/MyAccount/MyAccount'));
const RBExclusive = lazy(() => import('../Modules/RBExclusive/RBExclusive'));
const RBExclusivePDP = lazy(() => import('../Modules/ProductDetail/ExclusiveBookPDP/ExclusiveBookPDP'));
const ForPublishers = lazy(() => import('../Modules/Services/ForPulishers/ForPublishers'));
const BecomePartner = lazy(() => import('../Modules/Services/BecomePartner/BecomePartner'));
const ForAuthors = lazy(() => import('../Modules/Services/ForAuthors/ForAuthors'));
const PrintOnDemand = lazy(() => import('../Modules/Services/PrintOnDemand/PrintOnDemand'));
const CorporateGifts = lazy(() => import('../Modules/Services/CorporateGifts/CorporateGifts'));
const Checkout = lazy(() => import('../Modules/Checkout/Checkout'));
const ThankYou = lazy(() => import('../Modules/Checkout/ThankYou/ThankYou'));
const Blog = lazy(() => import('../Modules/Blogs/Blog/Blog'));
const Blogs = lazy(() => import('../Modules/Blogs/Blogs'));
const BestSeller = lazy(() => import('../Modules/BestSeller/BestSeller'));
const BestSellerPDP = lazy(() => import('../Modules/ProductDetail/BestSellerPDP/BestSellerPDP'));
const PrivacyPolicy = lazy(() => import('../Modules/PrivacyPolicy/PrivacyPolicy'));
const LimitedEdition = lazy(() => import('../Modules/LimitedEdition/LimitedEdition'));
const ReturnRefunds = lazy(() => import('../Modules/ReturnRefunds/ReturnRefunds'));
const TermsAndConditions = lazy(() => import('../Modules/TermsAndConditions/TermsAndConditions'));
const CreatePassword = lazy(() => import('../Modules/CreatePassword/CreatePassword'));

const GoogleAuthProvider = lazy(() => import("../Components/GoogleAuthProvider"));

const MyRouter = () => {
    const currentUser = useSelector((state) => state.user.currentUser);
    const dispatch = useDispatch()

    // Add this effect to force re-render when location changes via browser history
    useEffect(() => {
        // Set up a listener for history changes
        const unlisten = browserHistory.listen(() => {
            // Force a re-render by dispatching an action
            // This could be a custom action in your Redux store
            // Or you can use the existing action if appropriate
            // Alternatively, we can force scroll which causes React to update DOM
            window.scrollTo(0, 0);
        });
        
        // Clean up the listener when component unmounts
        return () => {
            unlisten();
        };
    }, []);

    const Protected = ({ routeProps, children }) => { 
        if (isUserLoggedIn() || currentUser?.id) {
            if(currentUser?.isVerified === false) {
                dispatch(verificationPendingActions.showMessage())
                return (
                <Redirect
                to={{
                pathname: urls.HOME,
                state: {
                    from: routeProps.location.pathname,
                },
                }}
            />)

            } else {
                return children;
            }
        } else {
            return (
            <Redirect
                to={{
                pathname: urls.LOGIN,
                state: {
                    from: routeProps.location.pathname,
                },
                }}
            />
            );
        }
    };

    const BookDetailRedirect = ({name}) => {
        const location = useLocation();
        const params = useParams();
        const searchParams = new URLSearchParams(location.search);
        const id = searchParams.get('id');
        const title = params.name

        //add a redirect to this sku ( based on client requirement )
        const redirectSku = "9781346009650";

        if (id) {
           if(id === redirectSku) {
            return <Redirect to={`/product/9780140280197/the-48-laws-of-power`} />;
           }
          return <Redirect to={`/listing/product/${id}/null`} />;
        } else if(name === "bestSeller" && title) {
            return <Redirect to={`/product/null/${title}`} />
        }
        
        // Fallback if no ID is found
        return <Redirect to="/" />;
    };

    // Redirect mapping for old product URLs
    const productRedirects = {
        '/product/null/the-gospel-of-the-holy-twelve': '/listing/product/1111008594336/the-gospel-of-the-holy-twelve',
        '/product/null/lord-of-the-rings-brown-leather-set': '/product/9780261102385/lord-of-the-rings-brown-leather-set',
        '/product/null/redwall-series': '/product/9781862301382/redwall-series',
        '/product/null/harry-potter-seven-volume-set': '/product/9781408856772/harry-potter-seven-volume-set',
        '/product/null/throne-of-glass-complete-series': '/product/9781526650535/throne-of-glass-complete-series?color=Multicolor+Set',
        '/product/null/house-of-leaves': '/product/9781417709045/house-of-leaves',
        '/product/null/oto-rituals-and-sex-magick': '/product/1111033400224/oto-rituals-and-sex-magick',
        "/best-seller-books/diagnostic-and-statistical-manual-of-mental-disorders-fifth-edition": "/listing/product/9780367324070/diagnosing-the-diagnostic-and-statistical-manual-of-mental-disorders-fifth-edition",
        "/product/null/the-synagogue-of-satan": "/listing/product/1111001975878/the-synagogue-of-satan",
        "/product/null/oto-rituals-and-sex-magick": "/product/1111033400224/oto-rituals-and-sex-magick",
        "/product/null/the-catcher-in-the-rye": "/product/1111015221723/the-catcher-in-the-rye",
        "/product/null/house-of-leaves": "/product/9781417709045/house-of-leaves",
        "/product/null/east-of-eden": "/product/9789394270794/east-of-eden",
        "/product/null/the-equinox-of-the-gods": "/product/1111033400225/the-equinox-of-the-gods",
        "/product/null/the-stormlight-archive-leather-bound-collectors-edition-the-way-of-kings-and-words-of-radiance": "/product/9789123592050/the-way-of-kings-and-words-of-radiance",
        "/product/null/the-satanic-bible-and-the-satanic-rituals": "/product/9780380013920/the-satanic-rituals",
        "/product/null/the-1599-geneva-bible": "/product/1111003541873/the-geneva-bible-1560",
        "/product/null/mere-christianity": "/product/9780060652920/mere-christianity",
        "/product/null/the-expanse-series": "/product/9780356504254/the-expanse-series",
        "/product/null/lord-of-the-rings-brown-leather-set": "/product/9780261102385/lord-of-the-rings-brown-leather-set",
        "/product/null/the-handmaids-tale": "/product/9780385490818/the-handmaids-tale",
        "/product/null/12-rules-for-life": "/product/9780141988511/12-rules-for-life",
        "/product/null/anne-of-green-gables-complete-series": "/product/1111021926596/anne-of-green-gables-eight-volume-set",
        "/product/null/the-invisible-life-of-addie-larue": "/product/9780765387561/the-invisible-life-of-addie-larue",
        "/product/null/the-song-of-achilles": "/product/9789389000221/the-song-of-achilles",
        "/product/null/fourth-wing": "/product/9781649374042/fourth-wing",
        "/product/null/emerald-tablets-of-thoth-the-atlantean-1930": "/listing/product/1111015896493/emerald-tablets-of-thoth-the-atlantean",
        "/product/null/the-complete-dead-sea-scrolls-in-english": "/product/9780141197319/the-complete-dead-sea-scrolls-in-english",
        "/product/null/the-cost-of-discipleship": "/product/9780684815008/the-cost-of-discipleship",
        "/product/null/gravitys-rainbow": "/product/9780143039945/gravitys-rainbow",
        "/product/null/azoetia": "/listing/product/1111015767249/azoetia",
        "/product/null/the-holy-books-of-thelema-five-volume-set": "/product/8888000932543/the-holy-books-of-thelema-five-volume-set",
        "/product/null/the-lesser-key-of-solomon-goetia": "/product/9789333319973/the-lesser-key-of-solomon-goetia",
        "/product/null/the-48-laws-of-power": "/product/9780140280197/the-48-laws-of-power",
        "/product/null/the-divine-pymander": "/product/8888007179750/the-divine-pymander",
        "/product/null/the-demonology-of-king-james-i": "/product/8888000952190/the-demonology-of-king-james-i",
        "/product/null/mastery": "/product/9780143124177/mastery",
        "/product/null/o-t-o-rituals-and-sex-magick": "/product/1111033400224/oto-rituals-and-sex-magick",
        "/product/null/a-full-collation-of-the-codex-sinaiticus": "/listing/product/1111011492770/a-full-collation-of-the-codex-sinaiticus-with-the-received-text-of-the-new-testament-to-which-is-prefixed-a-critical-introd-in-greek",
        "/product/null/the-stand": "/product/9781444720730/the-stand",
        "/product/null/mans-search-for-meaning": "/product/9781844132393/mans-search-for-meaning",
        "/product/null/zodiac-academy-the-awakening": "/listing/product/9781914425028/zodiac-academy-1-the-awakening-by-caroline-peckham-susanne-va",
        "/product/null/a-river-runs-through-it": "/listing/product/1111020606809/a-river-runs-through-it",
        "/product/null/collectanea-hermetica-10-volume-series-in-two-volumes": "/product/8888000945703/collectanea-hermetica-10-volume-series-in-two-volumes",
        "/product/null/a-tale-of-two-cities": "/product/1111007653847/a-tale-of-two-cities",
        "/product/null/deluxe-9-book-set-sancti-thomae-aquinatis-opera-omnia": "/listing/product/1111008516626/sancti-thomae-aquinatis-opera-omnia",
        "/product/null/the-7-habits-of-highly-effective-people": "/listing/product/1111021933254/the-seven-habits-of-highly-effective-people-restoring-the-character-ethic",
        "/product/null/casino-royale": "/listing/product/1111030275723/james-bond-casino-royale-roman",
        "/product/null/the-shining": "/product/9781444720723/the-shining",
        "/product/null/the-space-trilogy": "/product/9780007528417/the-space-trilogy",
        "/product/null/the-zend-avesta-3-volume-set": "/product/8888000952183/the-zend-avesta-3-volume-set",
        "/product/null/the-rudder-pedalion-of-the-metaphorical-ship-of-the-one-holy-catholic-church": "/listing/product/1111022205123/the-rudder-pedalion-of-the-metaphorical-ship-of-the-one-holy-catholic-and-apostolic-church-of-the-orthodox-christians",
        "/product/null/the-silmarillion-limited-edition": "/product/9780261102736/the-silmarillion-limited-edition",
        "/product/null/psychic-witch-limited-edition": "/product/9780738760841/psychic-witch-by-mat-auryn-limited-edition",
        "/product/null/the-republic-of-plato": "/listing/product/1111011352435/the-republic-of-plato-an-ideal-commonwealth-translated-by-benjamin-jowett-with-a-special-introd-by-william-cranston-lawton",
        "/product/null/the-meditations": "/product/8888000240648/the-meditations",
        "/product/null/the-geneva-bible": "/product/1111003541873/the-geneva-bible-1560",
        "/product/null/post-office-manual-1952": "/listing/product/1111006354696/post-office-manual",
        "/product/null/essential-works-of-aleister-crowley-in-one-set": "/product/1111015261582/the-complete-works-of-aleister-crowley-including-the-book-of-lies-magick-without-tears-the-diary-of-a-drug-fiend-and-more",
        "/product/null/magick-without-tears": "/product/1122330000943/magick-without-tears",
        "/product/null/project-hail-mary": "/product/9780593395561/project-hail-mary",
        "/product/null/the-hermetic-museum-2-volume-set": "/listing/product/9781015671683/the-hermetic-museum-volume-ii",
        "/product/null/the-chronicles-of-narnia-7-books-set": "/product/9780008663407/the-chronicles-of-narnia-7-books-set",
        "/product/null/complete-jewish-bible": "/listing/product/9781733935470/complete-jewish-bible-an-english-version-by-david-h-stern-giant-print",
        "/product/null/a-court-of-silver-flames": "/product/9781526632715/a-court-of-silver-flames",
        "/product/null/death-of-the-nile": "/product/9780062073556/death-on-the-nile",
        "/product/null/star-wars": "/product/9780345273833/star-wars",
        "/product/null/magick-in-theory-and-practice": "/product/8888006979153/magick-in-theory-and-practice",
        "/product/null/the-complete-works-of-austin-osman-spare": "/product/8888000944263/the-complete-works-of-austin-osman-spare",
        "/product/null/the-scarlet-letter": "/listing/product/1111011034952/the-scarlet-letter-a-romance-by-nathaniel-hawthorne",
        "/product/null/2-book-set-the-greater-key-of-solomon-the-lesser-key-of-solomon-goetia": "/product/1111003292872/the-lesser-key-of-solomon-goetia",
        "/product/null/the-book-of-enoch": "/listing/product/1111022216185/the-book-of-enoch",
        "/product/null/thrice-greatest-hermes-3-volume-set": "/listing/product/1111008956695/g-r-s-mead-thrice-greatest-hermes-vol-i",
        "/product/null/the-notebook": "/product/9780751540475/the-notebook",
        "/product/null/the-book-of-the-law": "/product/8888000925750/the-book-of-the-law",
        "/product/null/the-zend-avest-3-volume-set": "/product/8888000952183/the-zend-avesta-3-volume-set",
        "/product/null/irish-witchcraft-and-demonology-1913": "/listing/product/9781590210086/irish-witchcraft-demonology",
        "/product/null/beauty-and-the-beast-pocket-edition": "/product/9781683836971/beauty-and-the-beast-pocket-edition",
        "/product/null/3-book-set-the-complete-golden-dawn-system-of-magic": "/listing/product/9781561845712/the-complete-golden-dawn-system-of-magic-book-ii",
        "/product/null/the-greater-key-of-solomon": "/listing/product/1111000278889/the-greater-key-of-solomon",
        "/product/null/long-ago-told-huh-kew-ah-kah-legends-of-the-papago-indians-1926": "/listing/product/1111002199593/long-ago-told-huh-kew-ah-kah",
        "/product/null/the-prince": "/product/9789358210019/the-prince",
        "/product/null/the-diary-of-a-young-girl": "/product/1111015516730/the-diary-of-a-young-girl",
        "/product/null/the-complete-works-of-aleister-crowley-including-the-book-of-lies-magick-without-tears-the-diary-of-a-drug-fiend-and-more": "/product/1111015261582/the-complete-works-of-aleister-crowley-including-the-book-of-lies-magick-without-tears-the-diary-of-a-drug-fiend-and-more",
        "/product/null/fate-is-the-hunter": "/product/9781908059024/fate-is-the-hunter",
        "/product/null/the-secret": "/product/9781847370297/the-secret",
        "/product/null/combined-tolkien-edition-lord-of-the-rings-the-silmarillion-and-the-hobbit": "/product/9780261103566/combined-tolkien-edition-lord-of-the-rings-the-silmarillion-and-the-hobbit",
        "/product/null/a-court-of-thorns-and-roses-complete-series": "/product/9781526662224/a-court-of-thorns-and-roses-complete-series",
        "/product/null/game-of-thrones-complete-series": "/product/9781101965481/game-of-thrones-complete-series",
        "/product/null/meet-your-strawman-and-whatever-you-want-to-know": "/product/9781492877677/meet-your-strawman-and-whatever-you-want-to-know",
        "/product/null/duma-key": "/product/9781444707908/duma-key",
        "/product/null/deliverance-prayers-for-use-by-the-laity": "/product/9781541056718/deliverance-prayers-for-use-by-the-laity",
        "/product/null/salmes-lot": "/product/9781444708141/salems-lot",
        "/product/null/isis-unveiled-volume-2": "/listing/product/9781998614301/isis-unveiled-volume-2",
        "/product/null/desperation": "/product/9780670868360/desperation",
        "/product/null/chaldean-magic": "/listing/product/1111028006543/chaldean-magic-its-origin-and-development",
        "/product/null/moontouched": "/listing/product/9781999962906/moontouched",
        "/product/null/the-lord-of-the-rings-trilogy": "/product/9780008387754/the-lord-of-the-rings-trilogy",
        "/product/null/the-rosicrucians-their-rites-and-mysteries": "/listing/product/1111011136398/the-rosicrucians-their-rites-and-mysteries-with-chapters-on-the-ancient-fire-and-serpent-worshippers-and-explanations-of-the-mystic-symbols-represented-in-the-monuments-and-talismans-of-the-primeval-philosophers-by-hargrave-jennings",
        "/product/null/whence-the-black-irish-of-jamaica-1932": "/listing/product/1111015366664/whence-the-black-irish-of-jamaica",
        "/product/null/fairy-tale": "/product/9781399705417/fairy-tale",
        "/product/null/yoga": "/product/1122330001060/yoga",
        "/product/null/the-holy-books-of-thelema": "/product/1111033165521/the-holy-books-of-thelema",
        "/product/null/the-lion-the-witch-and-the-wardrobe": "/product/9780008663032/the-lion-the-witch-and-the-wardrobe",
        "/product/null/2-book-set-the-people-marx": "/product/9789333123099/2-book-set-the-people-marx",
        "/product/null/treasure-island": "/product/8888000985785/treasure-island",
        "/product/null/collectanea-hermetica-10-volume-set": "/product/8888000945710/collectanea-hermetica-10-volume-set",
        "/product/null/the-comprehensive-concodance-of-the-bible": "/product/8888000669609/the-comprehensive-concodance-of-the-bible",
        "/product/null/insomnia": "/product/9781444707854/insomnia",
        "/product/null/codex-sinaiticus-the-ancient-biblical-manuscript-now-in-the-british-museum": "/listing/product/1111022264331/codex-sinaiticus-the-ancient-biblical-manuscript-now-in-the-british-museum",
        "/product/null/the-witchcraft-duality-exploring-the-inner-and-outer-temples": "/product/9780738702766/the-witchcraft-duality-exploring-the-inner-and-outer-temples",
        "/product/null/collectanea-hermetica-10-volume-series-combined-in-two": "/product/8888000945703/collectanea-hermetica-10-volume-series-in-two-volumes",
        "/product/null/american-psycho": "/product/9780679735779/american-psycho",
        "/product/null/malleus-maleficarum": "/listing/product/1111008598799/malleus-maleficarum",
        "/product/null/in-cold-blood": "/product/9780141182575/in-cold-blood",
        "/product/null/don-quixote-de-la-mancha-3-volume-set": "/product/8888000911005/don-quixote-de-la-mancha-3-volume-set",
        "/product/null/1984-a-novel-1977": "/product/8888000092216/nineteen-eighty-four",
        "/product/null/the-alchemist": "/product/1111016050721/the-alchemist",
        "/product/null/the-nazarenes-songs": "/listing/product/1111010272521/the-nazarenes-songs-in-two-parts-the-second-part-never-before-published-by-william-gadsby",
        "/product/null/a-court-of-wings-and-ruin": "/product/9781526641175/a-court-of-wings-and-ruin",
        "/product/null/the-chronicles-of-narnia-the-classic-fantasy-adventure": "/product/9780066238500/the-chronicles-of-narnia-the-classic-fantasy-adventure",
        "/product/null/the-hobbit-special-edition": "/product/9780008376055_1/the-hobbit",
        "/product/null/atlantis-the-antediluvian-world": "/listing/product/9781976238154/atlantis-the-antedeluvian-world",
        "/product/null/clermont-4-volume-set": "/product/8888000944560/clermont-4-volume-set",
        "/product/null/the-eye-of-the-world": "/product/9781250251466/the-eye-of-the-world",
        "/product/null/the-frog-and-toad-collection": "/product/9780062983428/the-frog-and-toad-collection",
        "/product/null/the-book-of-wisdom-or-folly-1918": "/product/9789356981034/the-book-of-wisdom-or-folly-1918",
        "/product/null/his-dark-materials": "/product/9781841593425/his-dark-materials",
        "/product/null/masterwork-sabriel-lirael-abhorsen-across-the-wall": "/product/9780061474330/masterwork-sabriel-lirael-abhorsen-across-the-wall",
        "/product/null/dune": "/product/9780340960196/dune",
        "/product/null/stick-and-rudder": "/product/1111019106122/stick-and-rudder",
        "/product/null/777": "/product/8888000943532/777",
        "/product/null/crown-of-midnight": "/product/9781526635211/crown-of-midnight",
        "/product/null/exclusive-box-set-necronomicon": "/product/8888000922629/exclusive-box-set-the-children-of-abbey-a-tale",
        "/product/null/the-meditation": "/product/8888000240648/the-meditations",
        "/product/null/a-spring-harvest-1918": "/listing/product/1111024825707/a-spring-harvest",
        "/product/null/the-48-laws-of-power?utm_medium=social&utm_source=heylink.me": "/product/9780140280197/the-48-laws-of-power",
        "/product/null/the-last-unicorn": "/product/9780451450524/the-last-unicorn",
        "/product/null/the-33-strategies-of-war": "/product/9780143112785/the-33-strategies-of-war",
        "/product/null/konx-om-pax": "/product/8888000941972/konx-om-pax",
        "/product/null/the-lord-of-the-ring-illustration-edition": "/product/9780358653035/the-lord-of-the-ring-illustration-edition",
        "/product/null/atlas-shrugged": "/product/9780451191144/atlas-shrugged",
        "/product/null/night-shift": "/product/9780307947291/night-shift",
        "/product/null/fruit-from-a-poisonous-tree": "/product/9780595524969/fruit-from-a-poisonous-tree",
        "/product/null/the-magicians-nephew": "/product/9780008662950/the-magicians-nephew",
        "/product/null/harry-potter-and-the-half-blood-prince": "/product/9781408855706/harry-potter-and-the-half-blood-prince",
        "/product/null/braxton-bragg-the-most-hated-man-of-the-confederacy": "/product/9781799987383/braxton-bragg-the-most-hated-man-of-the-confederacy",
        "/product/null/under-the-dome": "/product/9780340992586/under-the-dome",
        "/product/null/the-history-of-the-decline-and-fall-of-the-roman-empire-6-volume-set": "/product/8888000945437/the-history-of-the-decline-and-fall-of-the-roman-empire-6-volume-set",
        "/product/null/the-book-of-lies": "/product/8888000941941/the-book-of-lies",
        "/product/null/shadow-and-bone": "/product/9781250027436/shadow-and-bone",
        "/product/null/dracula": "/product/1111000741441/dracula",
        "/product/null/the-concise-48-laws-of-power": "/product/9781861974044/the-concise-48-laws-of-power",
        "/product/null/a-court-of-thorns-and-roses": "/product/9781526641168/a-court-of-thorns-and-roses",
        "/product/null/demonology-and-devil-lore-2-volume-set": "/product/1111002596589/2-volume-set-demonology-and-devil-lore",
        "/product/null/conspirators-hierarchy-the-story-of-the-committee-of-300": "/listing/product/9781805402510/the-conspirators-hierarchy-the-story-of-the-committee-of-300",
        "/product/null/seal-sigil-call": "/product/9780738770536/seal-sigil-call",
        "/product/null/it": "/product/9781444707861/it",
        "/product/null/harry-potter-and-the-goblet-of-fire": "/product/9781408855683/harry-potter-and-the-goblet-of-fire",
        "/product/null/alices-adventures-in-wonderland": "/product/9788121214513/alices-adventures-in-wonderland",
        "/product/null/mastering-magick": "/product/9780738766041/mastering-magick",
        "/product/null/how-to-stop-worrying-and-start-living": "/product/1111015210140/how-to-stop-worrying-and-start-living",
        "/product/null/the-star-of-redemption": "/product/9780268017187/the-star-of-redemption",
        "/product/null/hero-with-a-thousand-faces": "/product/9789382742876/hero-with-a-thousand-faces",
        "/product/null/the-handmaids-tale-customized-edition": "/product/9780385490818/the-handmaids-tale",
        "/product/null/the-heart-of-the-master": "/product/1122330000992/the-heart-of-the-master",
        "/product/null/pride-and-prejudice": "/product/9789353241667/pride-and-prejudice",
        "/product/null/prince-caspian": "/product/9780008663087/prince-caspian",
        "/product/null/dark-trails-adventures-of-a-naturalist": "/listing/product/1111017261775/dark-trails-adventures-of-a-naturalist-by-george-k-cherrie-with-58-illustrations-and-a-map",
        "/product/null/harry-potter-and-the-philosophers-stone": "/product/9781408855898/harry-potter-and-the-philosophers-stone",
        "/product/null/masterpieces-of-stephen-king-it-the-shining-and-more-iconic-thrillers": "/product/9781529365726/masterpieces-of-stephen-king-it-the-shining-and-more-iconic-thrillers",
        "/product/null/nathan-bedford-forrest-a-biography": "/product/9780679748304/nathan-bedford-forrest-a-biography",
        "/product/null/pet-sematary": "/product/9781529378306/pet-sematary",
        "/product/null/a-court-of-frost-and-starlight": "/product/9781526617187/a-court-of-frost-and-starlight",
        "/product/null/godzilla-and-godzilla-raids-again": "/product/9781517915230/godzilla-and-godzilla-raids-again",
        "/product/null/the-invisible-life-of-addie-larue-brown-edition": "/product/9780765387561_1/the-invisible-life-of-addie-larue-brown-edition",
        "/product/null/harry-potter-and-the-order-of-the-phoenix": "/product/9781408855935/harry-potter-and-the-order-of-the-phoenix",
        "/product/null/12-rules-for-life-in-red-leather": "/product/9780141988511/12-rules-for-life",
        "/product/null/dreamcatcher": "/product/9781444707847/dreamcatcher",
        "/product/null/where-the-crawdads-sing": "/product/9781472154668/where-the-crawdads-sing",
        "/product/null/carrie": "/product/9781444720693/carrie",
        "/product/null/the-annotated-book-of-mormon": "/product/9780190082208_1/the-annotated-book-of-mormon-in-black-leather",
        "/product/null/the-satanic-legacy-rituals-and-bible-2-book-set": "/product/9780380015399/the-satanic-bible-and-the-satanic-rituals",
        "/product/null/the-story-of-my-experiments-with-truth": "/product/1122330001044/the-story-of-my-experiments-with-truth",
        "/product/null/jane-eyre": "/product/9789353241704/jane-eyre",
        "/product/null/gone-with-the-wind": "/product/9789353242060/gone-with-the-wind",
        "/product/null/crabbit": "/product/9781638600930/the-crabbit",
        "/product/null/interview-with-the-vampire": "/product/9780751541977/interview-with-the-vampire",
        "/product/null/throne-of-glass-complete-series?gQT=2": "/product/9781526650535/throne-of-glass-complete-series",
        "/product/null/crooked-kingdom": "/product/9781510107038/crooked-kingdom-special-edition",
        "/product/null/moonchild": "/product/8888000941958/moonchild",
        "/product/null/babel": "/product/9780008501822/babel-special-edition",
        "/product/null/the-book-of-the-low": "/product/8888000925750/the-book-of-the-law",
        "/product/null/the-children-of-the-abbey-a-tale": "/product/8888000922629/exclusive-box-set-the-children-of-abbey-a-tale",
        "/product/null/pistis-sophia": "/product/9789356982123/pistis-sophia",
        "/product/null/folk-witchcraft": "/product/9781736762509/folk-witchcraft",
        "/product/null/nineteen-eighty-four": "/product/8888000092216/nineteen-eighty-four"
        // Add more redirects here as needed
    };

    const blogRedirects = {
        '/blog/viridarium-umbris-by-daniel-a-schulke-a-journey-into-esoteric-traditions': '/blog/viridarium-umbris-a-journey-into-esoteric-traditions-1',
        '/blog/deciphering-the-mystery-discover-meet-your-strawman-by-david-e-robinson': '/blog/meet-your-strawman-understanding-the-hidden-legal-system-1'

        // Add more redirects here as needed
    };

    // Component to handle old product redirects
    const ProductRedirect = ({ match }) => {
        const redirectTo = productRedirects[match.url];
        return redirectTo ? <Redirect to={redirectTo} /> : <Error410Page />;
    };

    // Component to handle old blogs redirects
    const BlogRedirect = ({ match }) => {
        const redirectTo = blogRedirects[match.url];
        return redirectTo ? <Redirect to={redirectTo} /> : <Blog />;
    };


    return (
            <Switch>
                <Route
                    exact
                    path="/product/null/:title"
                    component={ProductRedirect}
                />
                <Route
                    exact
                    path={`${urls.VIEWBLOG}/:slug`}
                    component={BlogRedirect}
                />
                <Route path="/best-seller-books/:name">
                    <BookDetailRedirect name={"bestSeller"} />
                </Route>
                <Route exact path={`${urls.HOME}`}>
                    <Home />
                </Route>
                <Route exact path={`${urls.SIGNUP}`}>
                    <Suspense fallback={<SkeletonLoader />}>
                        <GoogleAuthProvider>
                            <SignUp />
                        </GoogleAuthProvider>
                    </Suspense>
                </Route>
                <Route exact path={`${urls.LOGIN}`}>
                    <Suspense fallback={<SkeletonLoader />}>
                        <GoogleAuthProvider>
                            <LoginComponent />
                        </GoogleAuthProvider>
                    </Suspense>
                </Route>
                <Route exact path={`${urls.FORGOTPASSWORD}`}>
                    <ForgotPassword />
                </Route>
                <Route exact path={`${urls.CREATEPASSWORD}`}>
                    <CreatePassword />
                </Route>
                <Route exact path={`${urls.CONTACTUS}`}>
                    <ContactUs />
                </Route>
                <Route
                    path={urls.USERPROFILE}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <MyAccount />
                        </Protected>
                    )}
                />
                <Route exact path={`${urls.FAQ}`}>
                    <FAQ />
                </Route>
                <Route exact path={`${urls.LISTING}`}>
                    <Listing />
                </Route>
                <Route path="/best-seller-books">
                    <Redirect to={urls.BESTSELLER} />
                </Route>
                <Route  path={urls.BESTSELLER}>
                    <BestSeller />
                </Route>
                <Route path={`${urls.BESTSELLERPDP}/:id/:name`}>
                    <BestSellerPDP />
                </Route>
                <Route path={`${urls.BESTSELLERPDP}/:name`}>
                    <BestSellerPDP />
                </Route>
                <Route path={`${urls.VIEWBLOG}/:slug`}>
                    <Blog />
                </Route>
                <Route path={`${urls.BLOG}`}>
                    <Blogs />
                </Route>
                <Route exact path="/books/detail">
                    <BookDetailRedirect />
                </Route>
                <Route exact path={`${urls.PRODUCTDETAIL}/:id/:name`}>
                    <ProductDetail />
                </Route>
                <Route exact path={`${urls.VIEWER}`}>
                    <Viewer />
                </Route>
                <Route exact path={urls.EDITOR}>
                    <Editor />
                </Route>
                <Route
                    path={`${urls.CUSTOMIZATION}/:id/:name`}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <Customization />
                        </Protected>
                    )}
                />
                <Route exact path={urls.SERVICES}>
                    <Services />
                </Route>

                <Route path="/customized-books">
                    <Redirect to={urls.SERVICES} />
                </Route>
                <Route exact path={urls.BOOKRESTORATION}>
                    <BookRestoration />
                </Route>
                <Route exact path={urls.PDFPRINTING}>
                    <PDFPrinting />
                </Route>
                <Route path="/print-books-from-pdf">
                    <Redirect to={urls.PDFPRINTING} />
                </Route>
                <Route exact path={urls.THESISBINDING}>
                    <ThesisBinding />
                </Route>
                <Route exact path={urls.LEATHERBINDING}>
                    <LeatherBinding />
                </Route>
                <Route path="/site/leather-book-binding-services">
                    <Redirect to={urls.LEATHERBINDING} />
                </Route>
                <Route exact path={urls.CART}>
                    <MyCart />
                </Route>
                <Route
                    path={urls.CHECKOUT}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <Checkout />
                        </Protected>
                    )}
                />
                <Route
                    path={urls.CHECKOUT_THANK_YOU}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <ThankYou />
                        </Protected>
                    )}
                />
                <Route exact path={urls.OURCOMPANY}>
                    <OurCompany />
                </Route>
                <Route exact path={urls.PRIVACYPOLICY}>
                    <PrivacyPolicy />
                </Route>
                <Route exact path={urls.LIMITEDEDITION}>
                    <LimitedEdition />
                </Route>
                <Route exact path={urls.RETURNREFUNDS}>
                    <ReturnRefunds />
                </Route>
                <Route exact path={urls.TERMSANDCONDITIONS}>
                    <TermsAndConditions />
                </Route>
                <Route path={`${urls.RB_EXCLUSIVE}/:id/:name`}>
                    <RBExclusivePDP />
                </Route>
                 <Route
                    path={urls.RB_EXCLUSIVE}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <RBExclusive />
                        </Protected>
                    )}
                />
                <Route exact path={urls.FORPUBLISHERS}>
                    <ForPublishers/>
                </Route>
                <Route exact path={urls.BECOMEPARTNER}>
                    <BecomePartner/>
                </Route>
                <Route exact path={urls.FORAUTHOR}>
                    <ForAuthors/>
                </Route>
                <Route exact path={urls.PRINTONDEMAND}>
                    <PrintOnDemand/>
                </Route>
                <Route exact path={urls.CORPORATEGIFTS}>
                    <CorporateGifts />
                </Route>
                
            </Switch>
    )
}

export default MyRouter